import * as Artifact from '../../util/artifacts';
import {toMongo} from '../../util/filters';
import {Filter} from '../../util/filterTypes';
import {FilterInfo, FilterValueTypes} from './MetadataFilterRow';

export const ARTIFACTS_PER_TYPE = 10;
export const VERSIONS_PER_SEQUENCE = 20;
export const ARTIFACT_POLL_INTERVAL = 5000; // 5 seconds

export function filterInfoToMongoFilter(
  filters: FilterInfo[],
  initalFilter: FilterInfo[]
): string {
  // Check for the default no filter state
  if (compareFiltersEqual(initalFilter, filters)) {
    return '';
  }
  const typedFilters: Array<Filter<Artifact.Key>> = [];
  filters.forEach(filter => {
    if (
      filter.key != null &&
      (filter.value != null || filter.type === FilterValueTypes.AnyValue)
    ) {
      switch (filter.type) {
        case FilterValueTypes.String:
          typedFilters.push({
            key: {section: 'artifact', name: `metadata.${filter.key}`},
            op: '=',
            value: filter.value || '',
          });
          break;
        case FilterValueTypes.Number:
          typedFilters.push({
            key: {section: 'artifact', name: `metadata.${filter.key}`},
            op: '=',
            value: Number(filter.value),
          });
          break;
        case FilterValueTypes.Boolean:
          typedFilters.push({
            key: {section: 'artifact', name: `metadata.${filter.key}`},
            op: '=',
            value: filter.value?.toLowerCase?.() === 'true',
          });
          break;
        case FilterValueTypes.AnyValue:
          typedFilters.push({
            key: {section: 'artifact', name: `metadata.${filter.key}`},
            op: 'EXISTS',
            value: true,
          });
          break;
        default:
          console.error('Failed to filter type');
      }
    }
  });
  const filtersString = JSON.stringify(
    toMongo({
      op: 'AND',
      filters: typedFilters,
    })
  );
  return filtersString;
}

export function filtersAreValid(
  filters: FilterInfo[],
  initalFilter: FilterInfo[]
) {
  // Check for the default no filter state
  if (compareFiltersEqual(initalFilter, filters)) {
    return true;
  }

  for (const filter of filters) {
    if (
      filter.key == null ||
      filter.key.length === 0 ||
      ((filter.value == null || filter.value.length === 0) &&
        filter.type !== FilterValueTypes.AnyValue)
    ) {
      return false;
    }

    if (filter.type === FilterValueTypes.Number) {
      if (Number.isNaN(Number(filter.value))) {
        return false;
      }
    }
    if (
      filter.type === FilterValueTypes.Boolean &&
      filter.value?.toLowerCase?.() !== 'true' &&
      filter.value?.toLowerCase?.() !== 'false'
    ) {
      return false;
    }
  }
  return true;
}

export function compareFiltersEqual(
  newFilter: FilterInfo[],
  oldFilter?: FilterInfo[]
) {
  if (
    (oldFilter === undefined || oldFilter.length === 0) &&
    newFilter.length === 0
  ) {
    return true;
  }
  if (oldFilter === undefined) {
    return false;
  }
  if (newFilter.length !== oldFilter.length) {
    return false;
  }

  for (let i = 0; i < newFilter.length; i++) {
    if (
      newFilter[i].key !== oldFilter[i].key ||
      newFilter[i].value !== oldFilter[i].value ||
      newFilter[i].type !== oldFilter[i].type
    ) {
      return false;
    }
  }

  return true;
}

export type ArtifactMembershipSidebar = {
  versionIndex: number;
  aliases: any[];
};

export type ArtifactSequenceSidebar = {
  name: string;
  id: string;
  artifactMemberships: {[versionIndex: number]: ArtifactMembershipSidebar};
};

export type ArtifactSideBarDataSearch = {
  artifactTypes: {
    [artifactTypeName: string]: {
      [sequenceName: string]: ArtifactSequenceSidebar;
    };
  };
};

export function formatArtifactMetadataSearchQuery(
  data: any
): ArtifactSideBarDataSearch | undefined {
  const artifacts = data?.project?.artifacts;
  const artifactList: ArtifactSideBarDataSearch = {
    artifactTypes: {},
  };
  if (artifacts == null || artifacts.length === 0) {
    return undefined;
  }

  for (const a of artifacts) {
    const atype = a.artifactType?.name;
    const aSequenceName = a.artifactSequence?.name;
    const aSequenceID = a.artifactSequence?.id;
    const aVersionIndex = a.versionIndex;
    if (
      aVersionIndex == null ||
      atype == null ||
      aSequenceName == null ||
      aSequenceID == null
    ) {
      continue;
    }
    const newMember: ArtifactMembershipSidebar = {
      versionIndex: a.versionIndex,
      aliases: a.aliases,
    };
    if (!(atype in artifactList.artifactTypes)) {
      artifactList.artifactTypes[atype] = {};
    }
    if (!(aSequenceName in artifactList.artifactTypes[atype])) {
      // Add new artifact sequence to list
      artifactList.artifactTypes[atype][aSequenceName] = {
        name: aSequenceName,
        id: aSequenceID,
        artifactMemberships: {},
      };
    }
    artifactList.artifactTypes[atype][aSequenceName].artifactMemberships[
      aVersionIndex
    ] = newMember;
  }

  return artifactList;
}
